/* Patamora v6: pestaña del −10 % que no tapa la compra, descripción bajo las fotos y carrito
   con sugerencias, cambio de color o talla y complemento con interruptor. */

import { CartLinesUpdateEvent } from '@shopify/events';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const docEl = document.documentElement;
const money = (cents) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(cents / 100);

/* ---------- Pestaña del −10 % ---------- */

// En la portada se esconde mientras el sofá ocupa la pantalla: ahí está el "Añadir al carrito".
// Aparece al terminar la animación, a la vez que la ventana de bienvenida (patamora.js).
const hero = document.querySelector('.pm-hero');
if (hero) {
  new IntersectionObserver(([entry]) => docEl.classList.toggle('pm-tab-tucked', entry.isIntersecting), {
    rootMargin: '0px 0px -60% 0px',
  }).observe(hero);
}

// En la ficha, con la barra fija de compra visible, la pestaña se queda solo en "−10 %".
const stickyBar = document.querySelector('.sticky-add-to-cart__bar');
if (stickyBar) {
  const sync = () => docEl.classList.toggle('pm-tab-compact', stickyBar.dataset.stuck === 'true');
  new MutationObserver(sync).observe(stickyBar, { attributes: true, attributeFilter: ['data-stuck'] });
  sync();
}

/* ---------- Ficha: la información baja con las fotos (escritorio) ---------- */

// Se copia el bloque bajo la galería; el CSS enseña la copia en escritorio y el original en móvil.
// Si Horizon repinta la galería al cambiar de variante, la copia se vuelve a poner.
document.querySelectorAll('product-component').forEach((component) => {
  const sync = () => {
    const media = component.querySelector('.product-information__media');
    const source = component.querySelector('.product-details .pm-info');
    if (!media || !source || media.querySelector(':scope > .pm-info')) return;
    const copy = source.cloneNode(true);
    copy.classList.add('pm-info--media');
    copy.removeAttribute('data-shopify-editor-block');
    media.append(copy);
    docEl.classList.add('pm-info-mirrored');
  };
  sync();
  new MutationObserver(sync).observe(component, { childList: true, subtree: true });
});

/* ---------- Carrito: variante de las sugerencias ---------- */

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-pm-upsell-variant]');
  if (!select) return;
  const item = select.closest('.pm-upsell__item');
  const option = select.selectedOptions[0];
  item.querySelector('[data-pm-add]').dataset.variant = select.value;
  const price = Number(option.dataset.price);
  const compare = Number(option.dataset.compare);
  item.querySelector('[data-pm-upsell-price]').textContent = money(price);
  const compareEl = item.querySelector('[data-pm-upsell-compare]');
  compareEl.hidden = !(compare > price);
  if (compare > price) compareEl.textContent = money(compare);
});

/* ---------- Carrito: peticiones que repintan el carrito lateral ---------- */

async function postJson(path, body) {
  const res = await fetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.description || data.message || 'No se ha podido actualizar el carrito.');
  return data;
}

const fetchCart = async () => (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();

// Avisa a Horizon con su evento estándar, ejecuta los cambios y le pasa las secciones nuevas para que repinte.
// `run` recibe la lista de secciones y devuelve la respuesta de la última petición (la que trae `sections`).
async function updateCart(source, action, lines, run) {
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  try {
    const data = await run(sections);
    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, sections: data.sections, source: 'patamora', didError: false },
    });
  } catch (error) {
    deferred.resolve({ detail: { didError: true, source: 'patamora' } });
    throw error;
  }
}

/* ---------- Carrito: cambiar color o talla de una línea ---------- */

// Sustituye la línea: añade la nueva variante con la misma cantidad (o la suma si ya estaba) y quita la vieja.
async function swapLineVariant(select) {
  const key = select.dataset.lineKey;
  const quantity = Number(select.dataset.quantity);
  const variantId = Number(select.value);
  const errorEl = select.closest('.cart-items__variants-wrapper')?.querySelector('[data-pm-line-error]');
  if (errorEl) errorEl.textContent = '';

  select.disabled = true;
  try {
    await updateCart(select, 'update', [{ id: key, quantity: 0 }], async (sections) => {
      const cart = await fetchCart();
      const twin = cart.items.find((i) => i.variant_id === variantId && i.key !== key);
      if (twin) await postJson('cart/change.js', { id: twin.key, quantity: twin.quantity + quantity });
      else await postJson('cart/add.js', { items: [{ id: variantId, quantity }] });
      return postJson('cart/change.js', { id: key, quantity: 0, sections, sections_url: window.location.pathname });
    });
  } catch (error) {
    select.value = select.dataset.current;
    select.disabled = false;
    if (errorEl) errorEl.textContent = error.message;
  }
}

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-pm-line-variant]');
  if (select && select.value !== select.dataset.current) swapLineVariant(select);
});

/* ---------- Carrito: complemento con interruptor (un clic lo añade o lo quita) ---------- */

document.addEventListener('change', async (event) => {
  const toggle = event.target.closest('[data-pm-addon]');
  if (!toggle) return;
  const on = toggle.checked;
  const variantId = Number(toggle.dataset.variant);
  const key = toggle.dataset.lineKey;
  toggle.disabled = true;
  try {
    await updateCart(
      toggle,
      on ? 'add' : 'remove',
      on ? [{ merchandiseId: String(variantId), quantity: 1 }] : [{ id: key, quantity: 0 }],
      (sections) =>
        on
          ? postJson('cart/add.js', { items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname })
          : postJson('cart/change.js', { id: key, quantity: 0, sections, sections_url: window.location.pathname })
    );
  } catch (error) {
    toggle.checked = !on;
    toggle.disabled = false;
  }
});
